import type { PlanCode, PrototypeRuntime, Root } from "../prototypeTypes";
import { closeMountedVideoPictureInPictureSession, ensureAudioPlaybackOwner, mountMediaPlayer, playMediaPlayer, pauseMediaPlayer, pauseMediaPlayerFromUser, replaceMountedVideoForExplicitTarget, toggleMediaPlayer, unmountMediaPlayer } from "../mediaPlayer";
import { requestExplicitPlaybackTokenWithOwnerRecovery } from '../playbackAuthorizationContext';
import { clearMediaNotReady, presentMediaNotReady, presentVideoNotReady } from '../mediaPlayerReadiness';
import { presentPlaybackAccessError } from '../mediaPlayerAccessGate';
import { clearPlaybackStartRetry } from '../mediaPlaybackFeedback';
import { closeVideoPresentationSurface, openVideoPresentationSurface } from '../videoPresentationSurface';
import {
	beginAudioHandoffNavigationIntent,
	beginMediaUserIntent,
	canCommitAudioHandoffExplicitPlay,
	isMediaUserIntentCurrent,
	targetAudioHandoffIntent,
	type AudioExplicitPlayIntent,
	type MediaUserIntent,
} from '../audioHandoffController';

export type AudioPlaybackResult = 'playing' | 'paused' | 'unavailable';

type VideoPlaybackTarget = { topicId: string; chapterIndex: number };
type AudioPlaybackTarget = { topicId: string; chapterIndex: number };

const videoPlaybackFlights = new WeakMap<object, { target: VideoPlaybackTarget; promise: Promise<void> }>();
const audioPlaybackFlights = new WeakMap<object, { target: AudioPlaybackTarget; promise: Promise<AudioPlaybackResult>; playIntent: boolean }>();

function sameVideoPlaybackTarget(left: VideoPlaybackTarget, right: VideoPlaybackTarget): boolean {
	return left.topicId === right.topicId && left.chapterIndex === right.chapterIndex;
}

function sameAudioPlaybackTarget(left: AudioPlaybackTarget, right: AudioPlaybackTarget): boolean {
	return left.topicId === right.topicId && left.chapterIndex === right.chapterIndex;
}

/**
 * Bridge between action handlers (which only know the user intent) and the
 * actual <audio>/<video> lifecycle. Token response from the worker carries
 * a signed URL bound to userId + sessionId; we mount it into the right
 * surface and ask the browser to play.
 *
 * On any error we surface a friendly hint via recordRuntimeError so the UI
 * can switch to a paywall/login modal without the user seeing a console line.
 */
export async function requestAudioPlayback(
  runtime: PrototypeRuntime,
  currentPlan: PlanCode,
  root: Root,
  recordRuntimeError: (runtime: PrototypeRuntime, error: unknown) => void,
	options: { play?: boolean; desiredPlaying?: () => boolean } = {},
	intent?: AudioExplicitPlayIntent,
): Promise<AudioPlaybackResult> {
	const target = { topicId: runtime.activeCourseId, chapterIndex: runtime.activeChapterIndex };
	const flightKey = root as unknown as object;
	// Only play-intent starts coalesce. A mount-paused restore (Back from
	// video fullscreen) must neither absorb a play request nor be absorbed by
	// one — absorbing either direction mounts the wrong final play state.
	const playIntent = options.play !== false && options.desiredPlaying?.() !== false;
	const activeFlight = audioPlaybackFlights.get(flightKey);
	if (activeFlight?.playIntent && playIntent && sameAudioPlaybackTarget(activeFlight.target, target)) return activeFlight.promise;
	const promise = requestAudioPlaybackOnce(runtime, currentPlan, root, recordRuntimeError, options, intent, target);
	audioPlaybackFlights.set(flightKey, { target, promise, playIntent });
	void promise.finally(() => {
		if (audioPlaybackFlights.get(flightKey)?.promise === promise) audioPlaybackFlights.delete(flightKey);
	});
	return promise;
}

async function requestAudioPlaybackOnce(
	runtime: PrototypeRuntime,
	currentPlan: PlanCode,
	root: Root,
	recordRuntimeError: (runtime: PrototypeRuntime, error: unknown) => void,
	options: { play?: boolean; desiredPlaying?: () => boolean },
	intent: AudioExplicitPlayIntent | undefined,
	target: AudioPlaybackTarget,
): Promise<AudioPlaybackResult> {
	// There may be no mounted audio yet. Registering here makes a remote claim
	// invalidate this pending first-play request before it can attach a URL.
	ensureAudioPlaybackOwner(root);
	const requestIntent = intent
		? targetAudioHandoffIntent(root, intent, target)
		: beginAudioHandoffNavigationIntent(root, target);
	if (!requestIntent) return 'unavailable';
	try {
		// Listen owns playback exactly like an explicit Watch: a live system PiP
		// window must close with the click, before the token await, because the
		// audio mount disposes the other kind's element and a disposed-but-
		// still-presented video strands an orphan black floating window.
		if (closeMountedVideoPictureInPictureSession(root)) closeVideoPresentationSurface(root);
		pauseMediaPlayer(root, 'video');
		const request = {
			topicId: target.topicId,
			chapterIndex: target.chapterIndex,
			plan: currentPlan,
		};
		const response = await requestExplicitPlaybackTokenWithOwnerRecovery(
			() => runtime.api.audioPlaybackToken(request),
		);
		if (!canCommitAudioHandoffExplicitPlay(root, requestIntent)) {
			releaseToken(runtime, response);
			return 'unavailable';
		}
		const mounted = mountMediaPlayer(root, response, target);
		if (mounted) {
			clearMediaNotReady(root, 'audio');
			// `play: false` mounts the audio paused (e.g. coming Back from a paused
			// video). Default keeps the historical autoplay-on-mount behavior.
			if (options.desiredPlaying?.() === false || options.play === false) {
				pauseMediaPlayer(root, 'audio');
				return 'paused';
			}
			return (await playMediaPlayer(root, 'audio')) ? 'playing' : 'paused';
		}
		releaseToken(runtime, response);
		return 'unavailable';
	} catch (error) {
		if (!canCommitAudioHandoffExplicitPlay(root, requestIntent)) return 'unavailable';
	if (presentMediaNotReady(root, 'audio', error)) {
		unmountMediaPlayer(root, 'audio', { force: true });
		return 'unavailable';
	}
	if (presentPlaybackAccessError(root, 'audio', error, target.topicId)) {
		unmountMediaPlayer(root, 'audio', { force: true });
		return 'unavailable';
	}
	recordRuntimeError(runtime, error);
		return 'unavailable';
	}
}

export async function requestVideoPlayback(
  runtime: PrototypeRuntime,
  currentPlan: PlanCode,
  root: Root,
  recordRuntimeError: (runtime: PrototypeRuntime, error: unknown) => void,
	intent?: MediaUserIntent,
	explicitTarget?: { topicId: string; chapterIndex: number },
): Promise<void> {
	const target = explicitTarget ?? { topicId: runtime.activeCourseId, chapterIndex: runtime.activeChapterIndex };
	const flightKey = root as unknown as object;
	const activeFlight = videoPlaybackFlights.get(flightKey);
	if (activeFlight && sameVideoPlaybackTarget(activeFlight.target, target)) return activeFlight.promise;
	const promise = requestVideoPlaybackOnce(runtime, currentPlan, root, recordRuntimeError, intent, target);
	videoPlaybackFlights.set(flightKey, { target, promise });
	void promise.finally(() => {
		if (videoPlaybackFlights.get(flightKey)?.promise === promise) videoPlaybackFlights.delete(flightKey);
	});
	return promise;
}

async function requestVideoPlaybackOnce(
	runtime: PrototypeRuntime,
	currentPlan: PlanCode,
	root: Root,
	recordRuntimeError: (runtime: PrototypeRuntime, error: unknown) => void,
	intent: MediaUserIntent | undefined,
	target: VideoPlaybackTarget,
): Promise<void> {
	const requestIntent = intent ?? beginMediaUserIntent(root);
	if (!isMediaUserIntentCurrent(root, requestIntent)) return;
	clearPlaybackStartRetry(root, 'video');
	openVideoPresentationSurface(root);
	// Browsing keeps PiP alive; an explicit Watch of another target does not.
	// This is intentionally before token fetch: the user's switch request closes
	// the old system PiP immediately, including when B later fails to load.
	replaceMountedVideoForExplicitTarget(root, target);
  try {
    pauseMediaPlayer(root, "audio");
    const response = await runtime.api.videoPlaybackToken({
		 topicId: target.topicId,
		 chapterIndex: target.chapterIndex,
      plan: currentPlan,
    });
		if (!isMediaUserIntentCurrent(root, requestIntent)) {
			releaseToken(runtime, response);
			return;
		}
    const mounted = mountMediaPlayer(root, response, target);
    if (mounted) {
      clearMediaNotReady(root, 'video');
      await playMediaPlayer(root, "video");
    }
		else releaseToken(runtime, response);
  } catch (error) {
		if (!isMediaUserIntentCurrent(root, requestIntent)) return;
	if (presentVideoNotReady(root, target.chapterIndex, error, {
	  retry: () => { void requestVideoPlayback(runtime, currentPlan, root, recordRuntimeError, undefined, target); },
	  continueReading: () => closeVideoPresentationSurface(root),
    })) {
		unmountMediaPlayer(root, 'video', { force: true });
		return;
	}
	if (presentPlaybackAccessError(root, 'video', error, target.topicId)) {
		unmountMediaPlayer(root, 'video', { force: true });
		return;
	}
	recordRuntimeError(runtime, error);
  }
}

function releaseToken(runtime: PrototypeRuntime, response: unknown): void {
	const leaseId = typeof (response as { playbackLeaseId?: unknown } | null)?.playbackLeaseId === 'string'
		? (response as { playbackLeaseId: string }).playbackLeaseId
		: undefined;
	if (leaseId) void runtime.api.releasePlaybackLease(leaseId).catch(() => undefined);
}

export function toggleAudioPlay(root: Root) {
  toggleMediaPlayer(root, "audio");
}

export function pauseAudio(root: Root) {
	pauseMediaPlayerFromUser(root, 'audio');
}
